import {
	type IntegrationAction,
	type InvestmentConversionIdentifierItems,
	type InvestmentIdentifierConversion,
	type InvestmentIdentifierImport,
	type InvestmentImportConverterType,
	type InvestmentImportResponse,
	type TemplateImportType,
	IntegrationsControllerV3ApiFactory,
} from "@eu.mdotm.api/sphere";
import { reportPlatformError } from "$root/api/error-reporting";
import { useApiGen } from "$root/api/hooks";
import { DropzoneArea } from "$root/components/DropzoneArea";
import BaseDropzoneBlock, { type Converted } from "$root/components/Upload/BaseDropzoneBlock";
import UploadButton from "$root/components/UploadButton";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import {
	capitalizeString,
	customObjectValuesFn,
	downloadContentDisposition,
	parallelize,
	parallelizeWithAllSettled,
} from "$root/utils";
import {
	type DialogProps,
	type Option,
	type TableColumn,
	ActionText,
	AutoSortTable,
	Banner,
	Collapsible,
	DefaultCollapsibleHeader,
	Dialog,
	DialogFooter,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import {
	Select,
	Checkbox,
	Button,
	IconButton,
	type FormProps,
	FormField,
	SubmitButton,
} from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import {
	type SpawnResult,
	adaptAnimatedNodeProvider,
	generateUniqueDOMId,
	spawn,
} from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef, ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { Set } from "immutable";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import XLSX from "xlsx";
import { z } from "zod";
import EmptyImportBox from "../instruments-editor/spawn/box/EmptyImportBox";
import ErrorImportBox from "../instruments-editor/spawn/box/ErrorImportBox";
import SuccessImportBox from "../instruments-editor/spawn/box/SuccessImportBox";
import { spawnChooseEntitySavingModeDialog } from "../instruments-editor/spawn/saving-mode";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { portfolioRepository } from "./repository";

//#region Shared constants & types
const ACCEPTED_FILE = "*";
const concurrency = 32;
type DropzoneState = "empty" | "error" | "success" | "loading";
type Id<T> = T & { id: string };

const commonDropzoneClassList = {
	empty: `bg-[color:var(--palette-N50)] border-dashed border-[color:var(--palette-N500)]`,
	success: `bg-[color:var(--palette-N0)] border-[color:var(--palette-P600)]`,
	error: `bg-[color:var(--palette-N0)] border-[color:var(--palette-D500)]`,
	loading: `bg-[color:var(--palette-N50)] border-dashed border-[color:var(--palette-N500)]`,
} satisfies Record<DropzoneState, string>;

const drawIcon = {
	empty: <EmptyImportBox />,
	error: <ErrorImportBox />,
	success: <SuccessImportBox />,
	loading: <EmptyImportBox />,
} satisfies Record<DropzoneState, ReactNode>;
//#endregion

//#region CoupleConversionBlock — paired composition/constraints upload (Amundi)
function readFileText(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			if (e.target?.result && typeof e.target?.result === "string") {
				resolve(e.target.result);
			}
		};

		reader.onerror = (e) => {
			reject(e);
		};

		reader.readAsText(file);
	});
}

const separator = "6b22a815-c996-45e8-b8ed-63349fad005a";

const coupleFormat = {
	composition: /^(?:\d{2})(?:(0[1-9]|1[0-2]))(?:(0[1-9]|[12]\d|3[01]))_Portafoglio_MDOTM$/gim,
	constraint: /^(?:\d{2})(?:(0[1-9]|1[0-2]))(?:(0[1-9]|[12]\d|3[01]))_Constraints_MDOTM$/gim,
};
// check if the date of the file are equals
function CoupleConversionBlock({
	disabled,
	onChange,
	onDelete,
	status = "empty",
}: {
	onChange(files: File[] | null): MaybePromise<void>;
	onDelete?(): void;
	disabled: boolean;
	status?: DropzoneState;
}) {
	const [selectedFiles, _setSelectedFiles] = useState<{
		composition: {
			file: null | File;
			error?: "INVALID_NAME" | "REQUIRED";
		};
		constraints: {
			file: null | File;
			error?: "INVALID_NAME";
		};
	}>({ composition: { file: null }, constraints: { file: null } });
	const latestSelectedFilesRef = useRef(selectedFiles);

	function setSelectedFiles(type: "composition" | "constraints", file: File | null) {
		_setSelectedFiles((prev) => {
			const clonedLatestSelectedFile = { ...prev };
			if (file) {
				const [name] = file.name.split("."); // ex name.constraint.csv
				if (type === "constraints" && !name.match(coupleFormat.constraint)) {
					clonedLatestSelectedFile[type] = { file: null, error: "INVALID_NAME" };
					return clonedLatestSelectedFile;
				}

				if (type === "composition" && !name.match(coupleFormat.composition)) {
					clonedLatestSelectedFile[type] = { file: null, error: "INVALID_NAME" };
					return clonedLatestSelectedFile;
				}
			}

			clonedLatestSelectedFile[type] = { file };
			latestSelectedFilesRef.current = clonedLatestSelectedFile;
			return clonedLatestSelectedFile;
		});

		if (
			latestSelectedFilesRef.current.composition.file !== null &&
			latestSelectedFilesRef.current.constraints.file !== null
		) {
			const files = customObjectValuesFn(latestSelectedFilesRef.current).flatMap((x) => (x.file ? [x.file] : []));
			unpromisify(async () => {
				const textFiles = await parallelize(files.map((x) => () => readFileText(x)));
				const csvResults = textFiles.join(`\n${separator}\n`);
				const blob = new Blob([csvResults], { type: "text/csv;charset=utf-8," });
				await onChange([
					new File(
						[blob],
						`${latestSelectedFilesRef.current.composition.file!.name}${
							latestSelectedFilesRef.current.constraints.file!.name
								? "," + latestSelectedFilesRef.current.constraints.file!.name
								: ""
						}`,
					),
				]);
			})();
		} else {
			unpromisify(() => onChange(null))();
		}
	}

	const { t } = useTranslation();

	return (
		<>
			<div className="flex gap-2">
				<DropzoneArea
					disabled={disabled}
					onChange={(file) => setSelectedFiles("composition", file)}
					accept={ACCEPTED_FILE}
					className="flex-1"
					childrenWrapperAppearance={{
						className: {
							[`relative rounded-sm flex p-2 gap-4 mb-5 justify-between items-center border-2`]: true,
							[status === "success" && !selectedFiles.composition.file?.name
								? commonDropzoneClassList["empty"]
								: commonDropzoneClassList[status]]: true,
						},
					}}
				>
					<div className="flex gap-2 items-center flex-1">
						{drawIcon[status]}
						<p className="font-semibold max-w-[160px] wrap-break-word">
							{selectedFiles.composition.file?.name
								? selectedFiles.composition.file.name
								: "Select the composition file or drag and drop here"}
						</p>
					</div>

					{selectedFiles.composition.file?.name ? (
						<IconButton
							unstyled
							onClick={() => {
								onDelete?.();
								setSelectedFiles("composition", null);
							}}
							icon="Delete"
							size={20}
							color={themeCSSVars.palette_N500}
						/>
					) : (
						<UploadButton
							loading={status === "loading"}
							size="small"
							label={t("BUTTON.SELECT")}
							onChange={(file) => setSelectedFiles("composition", file)}
							accept={ACCEPTED_FILE}
						/>
					)}
				</DropzoneArea>
				<DropzoneArea
					disabled={disabled || !selectedFiles.composition.file?.name}
					onChange={(file) => setSelectedFiles("constraints", file)}
					accept={ACCEPTED_FILE}
					className="flex-1"
					childrenWrapperAppearance={{
						className: {
							[`relative rounded-sm flex p-2 gap-4 mb-5 justify-between items-center border-2`]: true,
							[status === "success" && !selectedFiles.constraints.file?.name
								? commonDropzoneClassList["empty"]
								: commonDropzoneClassList[status]]: true,
						},
					}}
				>
					<div className="flex gap-2 items-center flex-1">
						{drawIcon[status]}
						<p className="font-semibold max-w-[160px] wrap-break-word">
							{selectedFiles.constraints.file?.name
								? selectedFiles.constraints.file.name
								: "Select the constraint file or drag and drop here"}
						</p>
					</div>

					{selectedFiles.constraints.file?.name ? (
						<IconButton
							unstyled
							onClick={() => {
								onDelete?.();
								setSelectedFiles("constraints", null);
							}}
							icon="Delete"
							size={20}
							color={themeCSSVars.palette_N500}
						/>
					) : (
						<UploadButton
							loading={status === "loading"}
							size="small"
							label={t("BUTTON.SELECT")}
							onChange={(file) => setSelectedFiles("constraints", file)}
							accept={ACCEPTED_FILE}
							disabled={!selectedFiles.composition.file?.name}
						/>
					)}
				</DropzoneArea>
			</div>
			{selectedFiles.composition.file === null && (
				<div className="mb-4">
					<Banner severity="info" title="Required file">
						Please upload portfolio composition before proceeding.
					</Banner>
				</div>
			)}
			{selectedFiles.composition.error === "INVALID_NAME" && (
				<div className="my-4">
					<Banner severity="error" title="Invalid composition file name">
						Please create a file with the following naming convention: &apos;aammgg_Portafoglio_MDOTM.csv&apos;.
					</Banner>
				</div>
			)}
			{selectedFiles.constraints.error === "INVALID_NAME" && (
				<div className="my-4">
					<Banner severity="error" title="Invalid constraints file name">
						Please create a file with the following naming convention: &apos;aammgg_Constraints_MDOTM.csv&apos;.
					</Banner>
				</div>
			)}
		</>
	);
}
//#endregion

//#region ConversionBlock — file upload & conversion check
function ConversionBlock({
	converter,
	status,
	converted,
	onSubmit,
	onChangeStatus,
}: {
	status: DropzoneState;
	onChangeStatus(status: DropzoneState): void;
	converter: InvestmentImportConverterType;
	onSubmit(conversions: Array<Converted<InvestmentConversionIdentifierItems>>): void;
	converted: Array<Converted<InvestmentConversionIdentifierItems>>;
}) {
	const { t } = useTranslation();
	const integrationsApiV3 = useApiGen(IntegrationsControllerV3ApiFactory);
	const reset = useCallback(() => {
		onChangeStatus("empty");
		onSubmit([]);
	}, [onChangeStatus, onSubmit]);

	const onChange = useCallback(
		async (files: File[], type: InvestmentImportConverterType) => {
			try {
				reset();
				onChangeStatus("loading");
				const filesToConverter = files.map(
					(file) => () =>
						axiosExtract(integrationsApiV3.convertCheckFrom(type, file)).then((response) => ({
							form: response,
							items: response,
						})),
				);

				const responseStack = await parallelizeWithAllSettled(filesToConverter, { concurrency });
				const assocFileWithConversion = files.map((file, i) => ({
					filename: file.name,
					conversion: responseStack[i].status === "fulfilled" ? responseStack[i].value.items : undefined,
				}));

				const succededResponses = assocFileWithConversion.filter(
					({ conversion }) => conversion !== undefined,
				) as Converted<InvestmentConversionIdentifierItems>[];
				const failedResponses = assocFileWithConversion.filter(
					({ conversion }) => conversion === undefined,
				) as Converted<undefined>[];

				if (type === "AMUNDI_CONVERTER" && responseStack[0]?.status === "fulfilled") {
					const { form, items } = responseStack[0].value;
					if (items.errors && items.errors?.length > 0) {
						const compositionErrorsMap = new Map(form.errors?.map((x) => [x.errorMessage!, x.investmentName!]) ?? []);
						const constraintErrors = items.errors?.filter((x) => {
							const compositionError = compositionErrorsMap.get(x.errorMessage!);
							return compositionError !== x.investmentName;
						});
						const file = files[0];
						const [compositionName, constraintName] = file.name.split(",");
						const conversions = [
							{
								filename: compositionName,
								conversion: {
									errors: form.errors,
									conversions: items.conversions,
								} satisfies InvestmentConversionIdentifierItems,
							},
							{
								filename: constraintName,
								conversion: {
									errors: constraintErrors,
									conversions: items.conversions,
								} satisfies InvestmentConversionIdentifierItems,
							},
						];

						return onSubmit(conversions);
					}
				}

				if (failedResponses.length > 0) {
					failedResponses.forEach((x) => {
						platformToast({
							children: `Failed to convert ${x.filename ?? "-"}`,
							severity: "error",
							icon: "Portfolio",
						});
					});
				}

				if (succededResponses.length > 0) {
					onSubmit(succededResponses);
				}

				if (failedResponses.length === succededResponses.length) {
					onChangeStatus("error");
				} else {
					onChangeStatus("success");
				}
			} catch (error) {
				onChangeStatus("error");

				throw error;
			}
		},
		[integrationsApiV3, onChangeStatus, onSubmit, reset],
	);

	const failedConversion = useMemo(() => converted.filter(({ conversion }) => conversion === undefined), [converted]);

	return (
		<>
			{match(converter)
				.with("AMUNDI_CONVERTER", () => (
					<CoupleConversionBlock
						disabled={status === "loading"}
						onChange={(x) => {
							if (x === null) {
								return reset();
							}

							unpromisify(() => onChange(x, converter))();
						}}
						status={status}
						onDelete={reset}
					/>
				))
				.otherwise(() => (
					<BaseDropzoneBlock
						converted={converted}
						disabled={status === "loading"}
						onDelete={reset}
						status={status}
						onChange={(x) => {
							if (x === null) {
								return reset();
							}

							unpromisify(() => onChange(x, converter))();
						}}
					/>
				))}
			{status === "loading" && (
				<div className="my-4">
					<Banner severity="info">{t("PORTFOLIOS.UPLOAD_DURATION_LIMIT")}</Banner>
				</div>
			)}
			{failedConversion.length > 0 && (
				<div className="mb-4">
					<Banner severity="warning" title="Failed to convert these files">
						{failedConversion.map(({ filename }) => filename).join(", ")}
					</Banner>
				</div>
			)}
		</>
	);
}
//#endregion

//#region OverrideTable — overwrite/ignore selection for existing portfolios
function OverrideTable({
	selected,
	onChange,
	rows,
}: {
	rows: Array<InvestmentIdentifierConversion & { id: string }>;
	onChange(value: Set<string>): void;
	// selected: MutableRefObject<Set<string>>;
	selected: Set<string>;
}) {
	const onChangeRef = useUnsafeUpdatedRef(onChange);
	const rowsId = useMemo(() => rows.map(({ id }) => id), [rows]);
	const columns = useMemo<TableColumn<Id<InvestmentIdentifierConversion>>[]>(
		() => [
			{
				name: "name",
				header: "",
				content: (row) => row.investmentImport?.name ?? "-",
				minWidth: 200,
			},
			{
				name: "overwrite",
				header: (headerProps) => (
					<Row {...headerProps} gap={8} alignItems="center">
						<Checkbox
							checked={
								selected.size === 0
									? true
									: selected.size === rowsId.length
										? false
										: selected.size > 0
											? "indeterminate"
											: selected.size === 0
							}
							onChange={() => onChangeRef.current(Set())}
						>
							<Text type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500} className="truncate">
								Overwrite
							</Text>
						</Checkbox>
					</Row>
				),
				content: ({ id }, cellProps) => (
					<Row {...cellProps} alignItems="center">
						<Checkbox checked={!selected.has(id)} onChange={() => onChangeRef.current(selected.remove(id))} />
					</Row>
				),
				width: 120,
			},
			{
				name: "ignore",
				header: (headerProps) => (
					<Row {...headerProps} gap={8} alignItems="center">
						<Checkbox
							checked={
								selected.size > 0 && selected.size < rowsId.length ? "indeterminate" : selected.size === rowsId.length
							}
							onChange={() => onChangeRef.current(Set(rowsId))}
						>
							<Text type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500} className="truncate">
								Ignore
							</Text>
						</Checkbox>
					</Row>
				),
				content: ({ id }, cellProps) => (
					<Row {...cellProps} alignItems="center">
						<Checkbox
							checked={selected.has(id)}
							onChange={() => {
								onChangeRef.current(selected.add(id));
								console.log("ignore");
							}}
						/>
					</Row>
				),
				width: 120,
			},
		],
		[onChangeRef, rowsId, selected],
	);

	return (
		<AutoSortTable
			rows={rows}
			columns={columns}
			className="bg-transparent! max-h-[200px]"
			rowClassName="bg-transparent! border-[#A0A7B6]!"
			style={{ backgroundColor: "transparent" }}
		/>
	);
}
//#endregion

//#region Converter maps — template download & import API dispatch
const converterToTemplateType = {
	AMUNDI_CONVERTER: "AMUNDI_PORTFOLIO_IMPORT",
	ANALYSIS_CONVERTER: "ANALYSIS_PORTFOLIO_IMPORT",
	BLOOMBERG_CONVERTER: "BLOOMBERG_PORTFOLIO_IMPORT",
	GUARDIAN_CONVERTER: "GUARDIAN_PORTFOLIO_IMPORT",
	SPHERE_TEMPLATE_CONVERTER: "SPHERE_PORTFOLIO_IMPORT",
	UNIPOL_CONVERTER: "SPHERE_PORTFOLIO_IMPORT", // added as a silent type error but templateImportType doesn't exist
	EASIM_CONVERTER: "EASIM_INVESTMENT_IMPORT",
	SPHERE_BULK_TEMPLATE_CONVERTER: "SPHERE_BULK_IMPORT",
} satisfies { [key in InvestmentImportConverterType]: TemplateImportType };

type ImportInvestmentsParams = {
	api: ReturnType<typeof IntegrationsControllerV3ApiFactory>;
	items: Array<{ integrationAction: IntegrationAction; investment: InvestmentIdentifierImport }>;
	progressCb(progress: number): void;
};

type ImportInvestmentsResult = {
	imported: InvestmentImportResponse[];
	failed: Array<{ investment: InvestmentIdentifierImport; err: unknown }>;
	draft: Array<{ investment: InvestmentIdentifierImport; msg: unknown }>;
};

async function importInvestmentsSingularly({
	api,
	items,
	progressCb,
}: ImportInvestmentsParams): Promise<ImportInvestmentsResult> {
	let progress = 0;
	const imported: ImportInvestmentsResult["imported"] = [];
	const failed: ImportInvestmentsResult["failed"] = [];
	const draft: ImportInvestmentsResult["draft"] = [];

	const callStack = items.map(
		({ integrationAction, investment }) =>
			() =>
				api
					.importInvestmentAsync(integrationAction, investment)
					.then((resp) => {
						if (resp.data.status === "ERROR") {
							failed.push({ investment, err: resp.data.message });
							return;
						}

						if (resp.data.status === "DRAFT") {
							draft.push({ investment, msg: "Portfolio is being import in draft status" });
						}

						imported.push(resp.data);
					})
					.catch((err) => failed.push({ investment, err }))
					.finally(() => {
						progress++;
						progressCb(progress / callStack.length);
					}),
	);
	await parallelize(callStack, { concurrency });

	return { imported, failed, draft };
}

async function importInvestmentsBulk({
	api,
	items,
	progressCb,
}: ImportInvestmentsParams): Promise<ImportInvestmentsResult> {
	const imported: ImportInvestmentsResult["imported"] = [];
	const failed: ImportInvestmentsResult["failed"] = [];
	const draft: ImportInvestmentsResult["draft"] = [];

	const investments = items.map(({ integrationAction, investment }) => ({ ...investment, integrationAction }));
	try {
		const responses = await axiosExtract(api.importInvestmentsBulkMixed({ investments }));
		responses.forEach((resp, i) => {
			const investment = investments[i] ?? {};
			if (resp.status === "ERROR") {
				failed.push({ investment, err: resp.message });
				return;
			}

			if (resp.status === "DRAFT") {
				draft.push({ investment, msg: "Portfolio is being import in draft status" });
			}

			imported.push(resp);
		});
	} catch (err) {
		for (const investment of investments) {
			failed.push({ investment, err });
		}
	} finally {
		progressCb(1);
	}

	return { imported, failed, draft };
}

const converterToImportApi = {
	AMUNDI_CONVERTER: importInvestmentsSingularly,
	ANALYSIS_CONVERTER: importInvestmentsSingularly,
	BLOOMBERG_CONVERTER: importInvestmentsSingularly,
	GUARDIAN_CONVERTER: importInvestmentsSingularly,
	SPHERE_TEMPLATE_CONVERTER: importInvestmentsSingularly,
	UNIPOL_CONVERTER: importInvestmentsSingularly,
	EASIM_CONVERTER: importInvestmentsSingularly,
	SPHERE_BULK_TEMPLATE_CONVERTER: importInvestmentsBulk,
} satisfies {
	[key in InvestmentImportConverterType]: (params: ImportInvestmentsParams) => Promise<ImportInvestmentsResult>;
};

const uuidSchema = z.string().uuid();

/** A composition names a portfolio it holds by that portfolio's UUID, which is what the backend checks. */
function containsPortfolio(investment: InvestmentIdentifierImport): boolean {
	return investment.composition?.some(({ ticker }) => uuidSchema.safeParse(ticker).success) ?? false;
}
//#endregion

//#region InvestmentImportDialogV2 — main dialog
type InvestmentImportDialogPropsV2 = {
	converters: Array<InvestmentImportConverterType>;
	onImportFinished(params: {
		imported: Array<InvestmentImportResponse>;
		failed: Array<{ investment: InvestmentIdentifierImport; err: unknown }>;
	}): void;
	qualifier?: string;
	show: boolean;
	onClose(): void;
	onAnimationStateChange?: DialogProps["onAnimationStateChange"];
};

function InvestmentImportDialogV2({
	converters,
	onImportFinished,
	show,
	onClose,
	onAnimationStateChange,
}: InvestmentImportDialogPropsV2): React.JSX.Element {
	const { t } = useTranslation();
	const options = useMemo(
		() =>
			converters.map(
				(converter): Option<InvestmentImportConverterType> => ({
					label: capitalizeString(t(`IMPORT.${converter}`).toLowerCase()),
					value: converter,
				}),
			),
		[converters, t],
	);

	const [selectedConverter, setSelectedConverter] = useState(converters[0]);
	const [converted, setConverted] = useState<Array<Converted<InvestmentConversionIdentifierItems>>>([]);
	const [dropzoneStatus, setDropzoneStatus] = useState<DropzoneState>("empty");

	const [investmentToIgnore, _setInvestmentToIgnore] = useState(Set<string>());
	const latestInvestmentToIgnore = useRef(investmentToIgnore);
	function setInvestmentToIgnore(investments: Set<string>) {
		_setInvestmentToIgnore(investments);
		latestInvestmentToIgnore.current = investments;
	}

	const flatConversion = useMemo(
		() => converted.flatMap((x) => x.conversion.conversions ?? []).map((x) => ({ ...x, id: generateUniqueDOMId() })),
		[converted],
	);
	const existingInvestments = useMemo(() => {
		return flatConversion.filter(
			({ integrationAction }) => integrationAction === "ENHANCEMENT" || integrationAction === "EDIT_COMPOSITION",
		);
	}, [flatConversion]);

	const { overridables, unconsistent } = useMemo(
		() =>
			existingInvestments.reduce<{
				overridables: Array<Id<InvestmentIdentifierConversion>>;
				unconsistent: Array<Id<InvestmentIdentifierConversion>>;
			}>(
				(acc, el) => {
					if (el.integrationStatus === "READY_FOR_ACTIONS") {
						acc.overridables.push(el);
						return acc;
					}
					acc.unconsistent.push(el);
					return acc;
				},
				{
					overridables: [],
					unconsistent: [],
				},
			),
		[existingInvestments],
	);

	const conversionWithErrors = useMemo(
		() => converted.filter((x) => x.conversion.errors && x.conversion.errors.length > 0),
		[converted],
	);

	const cleanConvertion = useMemo(
		() =>
			conversionWithErrors.length === 0 &&
			overridables.length === 0 &&
			unconsistent.length === 0 &&
			converted.length > 0,
		[conversionWithErrors.length, converted.length, overridables.length, unconsistent.length],
	);

	const disabled = useMemo(() => {
		const common = conversionWithErrors.length > 0 || converted.length === 0;
		return common && (unconsistent.length === 0 || unconsistent.length === flatConversion.length);
	}, [conversionWithErrors.length, converted.length, flatConversion.length, unconsistent.length]);

	const integrationsApiV3 = useApiGen(IntegrationsControllerV3ApiFactory);

	const onSubmitAsync = useCallback<FormProps<void>["onSubmitAsync"]>(
		async (_, { progressCb }) => {
			const fileToSubmit = flatConversion.flatMap(
				({
					id,
					...investments
				}): Array<{
					integrationAction: IntegrationAction;
					investment: InvestmentIdentifierImport;
				}> => {
					const investment = investments.investmentImport;
					const integrationAction = investments.integrationAction;
					if (investmentToIgnore.has(id) || !investment || !integrationAction) {
						return [];
					}

					return [{ integrationAction, investment }];
				},
			);

			if (fileToSubmit.length === 0) {
				platformToast({
					children: "No portfolio imported",
					severity: "info",
					icon: "Dowload",
				});
				onImportFinished({ failed: [], imported: [] });
				return;
			}

			if (fileToSubmit.some(({ investment }) => containsPortfolio(investment))) {
				const portfolioSavingMode = await spawnChooseEntitySavingModeDialog({ portfolioSavingMode: "NEST_PORTFOLIOS" })
					.promise;
				if (!portfolioSavingMode) {
					return;
				}
				for (const item of fileToSubmit) {
					if (containsPortfolio(item.investment)) {
						item.investment = { ...item.investment, portfolioSavingMode };
					}
				}
			}

			const { imported, failed, draft } = await converterToImportApi[selectedConverter]({
				api: integrationsApiV3,
				items: fileToSubmit,
				progressCb,
			});

			portfolioRepository.invalidateLists();

			trackMixPanelEvent("portfolio.import", {
				failed: failed.map((f) => (f.err ? String(f.err) : "unknown")),
				importedAsDrafts: draft.length,
				importedAsPortfolios: imported.length,
				format: selectedConverter,
			});

			if (failed.length === 0) {
				platformToast({
					children: "Sphere has taken over your request",
					severity: "info",
					icon: "Dowload",
				});
			} else if (failed.length === fileToSubmit.length) {
				platformToast({
					children: "No portfolio imported",
					severity: "error",
					icon: "Dowload",
				});
			} else {
				if (draft.length > 0) {
					platformToast({
						children: `Imported ${draft.length} in draft status`,
						severity: "warning",
						icon: "Dowload",
					});
				}

				platformToast({
					children: `Imported ${fileToSubmit.length - failed.length} out of ${fileToSubmit.length} portfolios`,
					severity: "warning",
					icon: "Dowload",
				});
			}

			onImportFinished({ imported, failed });
		},
		[flatConversion, integrationsApiV3, investmentToIgnore, onImportFinished, selectedConverter],
	);

	const handleDownloadErrorReport = useCallback((report: Array<Converted<InvestmentConversionIdentifierItems>>) => {
		const xlsSheet = report.flatMap(
			({ conversion, filename }) =>
				conversion.errors?.map(({ errorMessage, investmentName }) => ({
					File: filename,
					Portfolio: investmentName,
					Message: errorMessage,
				})) ?? [],
		);

		const reportWS = XLSX.utils.json_to_sheet(xlsSheet);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, reportWS, "Report Errors");
		XLSX.writeFile(wb, "Errors file.xlsx");
	}, []);

	const handleDownloadTemplate = useCallback(
		(convertTo: InvestmentImportConverterType) =>
			integrationsApiV3
				.getUploadTemplate(converterToTemplateType[convertTo], {
					responseType: "blob",
				})
				.then(downloadContentDisposition)
				.catch((e) => {
					platformToast({ children: "Oops, Something went wrong, pleas re-try", severity: "error", icon: "Portfolio" });
					reportPlatformError(e, "ERROR", "portfolio", { message: "unable to download template" });
					throw e;
				}),
		[integrationsApiV3],
	);

	return (
		<Dialog
			size="large"
			show={show}
			onClose={onClose}
			onSubmitAsync={onSubmitAsync}
			onAnimationStateChange={onAnimationStateChange}
			header="Import file"
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton palette="primary" disabled={disabled}>
							{t("BUTTON.SAVE")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<div className="mb-4">
				<FormField label="File format">
					{(forward) => (
						<Select
							{...forward}
							options={options}
							value={selectedConverter}
							onChange={(converter) => {
								setConverted([]);
								setDropzoneStatus("empty");
								if (converter) {
									setSelectedConverter(converter);
								}
							}}
							className="w-[300px]"
						/>
					)}
				</FormField>
			</div>
			<ol className="list-decimal ml-4 mb-4">
				<li>
					Download the <ActionText onClickAsync={() => handleDownloadTemplate(selectedConverter)}>template</ActionText>
				</li>
				<li>Insert your portfolio composition</li>
				<li>Upload the edited template. Only files formatted like the template will be accepted.</li>
			</ol>
			<ConversionBlock
				converted={converted}
				onSubmit={setConverted}
				status={dropzoneStatus}
				onChangeStatus={setDropzoneStatus}
				converter={selectedConverter}
			/>

			{cleanConvertion && dropzoneStatus === "success" && (
				<div className="mb-4">
					<Banner severity="success" title="File uploaded correctly">
						Your portfolios have been converted by Sphere. Click &quot;
						<strong>Save</strong>&quot; to start analyzing and processing your instruments.
					</Banner>
				</div>
			)}

			{overridables.length > 0 && (
				<div className="mb-4 space-y-4">
					<Banner severity="warning" title="Update existing portfolios">
						<p className="mb-2">Some portfolios already exist. Would you like to overwrite them?</p>
					</Banner>
					<OverrideTable rows={overridables} selected={investmentToIgnore} onChange={setInvestmentToIgnore} />
				</div>
			)}

			{unconsistent && unconsistent.length > 0 && (
				<div className="mb-4">
					<Banner severity="error" title="Some portfolios cannot be updated in this state.">
						Import{" "}
						<strong>
							{unconsistent
								.map((portfolios) => portfolios.investmentImport?.name)
								.join(", ")
								.toString()}{" "}
						</strong>
						had a ongoing process. You must edit your portfolios before proceeding with the update.
					</Banner>
				</div>
			)}
			{conversionWithErrors.length > 0 && (
				<Banner severity="error" title="Please review the template format and upload the file again" className="mb-4">
					<p>The uploaded files have some formatting errors</p>
					{"\n"}
					<ActionText as="button" onClick={() => handleDownloadErrorReport(conversionWithErrors)}>
						Download errors file
					</ActionText>
				</Banner>
			)}
			<ForEach collection={conversionWithErrors}>
				{(item) => (
					<Collapsible
						header={(params) => (
							<DefaultCollapsibleHeader
								{...params}
								className={{
									[`bg-transparent! min-h-[40px] flex items-center border-b border-[var(--palette-N200)]`]: true,
								}}
							>
								{item.filename}
							</DefaultCollapsibleHeader>
						)}
						className="mb-2"
					>
						<ForEach collection={item.conversion.errors ?? []}>
							{(e) => (
								<div className="h-[40px] border-b border-[#eeeef1] transition-[background] data-forceHover:bg-[#EFF0F3] hover:bg-[#EFF0F3] flex space-x-2 items-center px-2">
									<p className="w-14 truncate shrink-0">{e.investmentName}</p>
									<p className="p-2">{e.errorMessage}</p>
								</div>
							)}
						</ForEach>
					</Collapsible>
				)}
			</ForEach>
		</Dialog>
	);
}
//#endregion

//#region Spawn entry point
type SpawnInvestmentImportDialogParamsV2 = Omit<InvestmentImportDialogPropsV2, "onClose" | "show">;
export function spawnInvestmentImportDialogV2(params: SpawnInvestmentImportDialogParamsV2): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<InvestmentImportDialogV2
				{...params}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onImportFinished={(...forward) => {
					resolve();
					params.onImportFinished(...forward);
				}}
			/>
		)),
	);
}
//#endregion

export default InvestmentImportDialogV2;
